import { AxiosRequestConfig } from 'axios';
import { getApiInstance } from 'services/Api';

type SerializedDocument = {
	serializedData: string;
	blobData: Blob;
	displayAsPdf: boolean;
	contentType: string;
} | null;

export const previewDocumentById = async (
	documentId: string,
	date: string,
	title: string
): Promise<SerializedDocument> => {
	try {
		const apiInstance = await getApiInstance('/commercial-selfservice/');

		const response = await apiInstance?.documentApiServiceLayer?.fetchCustomerDocument(documentId, requestHeaders);

		const data = response.data as Blob;
		const contentType = data.type;

		return serializeDocumentType(contentType, data, title, date);
	} catch (error) {
		console.error('fetchCustomerDocument: Error fetching document', error);
		throw error;
	}
};

export const previewPolicyById = async (policyNumber: string): Promise<SerializedDocument> => {
	try {
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		const response = await apiInstance.documentApiServiceLayer.fetchPolicyDocument(policyNumber, requestHeaders);

		const data = response.data as Blob;
		const contentType = data.type;

		return serializeDocumentType(contentType, data, policyNumber);
	} catch (error) {
		console.error('fetchCustomerDocument: Error fetching document', error);
		throw error;
	}
};

const serializeDocumentType = async (contentType: string, data: BlobPart, title: string, date?: string) => {
	switch (contentType) {
		case 'application/xml': {
			const blob = new Blob([data], { type: 'application/xml' });
			const htmlContent = await xmlBlobToHtmlView(blob);

			if (htmlContent) {
				const htmlBlob = new Blob([buildHtmlDocument(sanitizeHtmlForPreview(htmlContent))], {
					type: 'text/html;charset=utf-8',
				});
				return {
					serializedData: window.URL.createObjectURL(htmlBlob),
					blobData: blob,
					displayAsPdf: true,
					contentType: 'application/xml',
				};
			}

			return {
				serializedData: window.URL.createObjectURL(blob),
				blobData: blob,
				displayAsPdf: true,
				contentType: 'application/xml',
			};
		}

		case 'text/plain': {
			const blob = new Blob([data], { type: 'text/plain' });
			return {
				serializedData: await plainBlobToHtmlView(blob),
				blobData: blob,
				displayAsPdf: false,
				contentType,
			};
		}

		case 'application/pdf': {
			const blob = new Blob([data], { type: 'application/pdf' });
			const pdfUrl = window.URL.createObjectURL(blob);
			return { serializedData: pdfUrl, blobData: blob, displayAsPdf: true, contentType };
		}

		case 'application/octet-stream': {
			const blob = new Blob([data], { type: contentType });
			downloadBlob(blob, title, 'eml', date);
			return null;
		}

		default: {
			const fileExt = getFileExtension(contentType);
			const blob = new Blob([data], { type: contentType });

			downloadBlob(blob, title, fileExt, date);
			return null;
		}
	}
};

const xmlBlobToHtmlView = (blob: Blob): Promise<string | null> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		const parser = new DOMParser();

		reader.addEventListener('loadend', (event) => {
			const text = event.target?.result as string;
			const xmlDoc = parser.parseFromString(text, 'text/xml');
			const parserError = xmlDoc.getElementsByTagName('parsererror')[0];
			if (parserError) {
				resolve(null);
				return;
			}

			const contentNode = Array.from(xmlDoc.getElementsByTagName('*')).find(
				(element) => element.getAttribute('name') === 'Content'
			);

			if (!contentNode) {
				resolve(null);
				return;
			}

			const htmlContent = contentNode.textContent?.trim() || '';
			resolve(htmlContent || null);
		});

		reader.onerror = (error) => {
			reject(error);
		};

		reader.readAsText(blob);
	});
};

const plainBlobToHtmlView = (blob: Blob): Promise<string | null> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.addEventListener('loadend', (event) => {
			const textContent = event.target?.result as string;
			if (textContent) resolve(textContent);
			else reject();
		});
		reader.readAsText(blob);
	});
};

const buildHtmlDocument = (body: string): string => `
	<!doctype html>
	<html>
	<head>
		<meta charset="utf-8">
		<style>
			html, body { margin: 0; padding: 0; background: #fff; font-family: Amelia, sans-serif; font-size: 14px; }
			body { padding: 24px; box-sizing: border-box; }
		</style>
	</head>
	<body>${body}</body>
	</html>`;

const sanitizeHtmlForPreview = (htmlContent: string): string => {
	const parser = new DOMParser();
	const htmlDoc = parser.parseFromString(htmlContent, 'text/html');

	htmlDoc.querySelectorAll('style, link[rel="stylesheet"]').forEach((element) => element.remove());
	htmlDoc.querySelectorAll('*').forEach((element) => {
		element.removeAttribute('style');
		element.removeAttribute('class');
	});

	return htmlDoc.body.innerHTML;
};

export const getFileExtension = (contentType: string): string => {
	const parts = contentType.split('/');
	return parts.length > 1 ? parts[1] : 'bin';
};

export const formatDate = (dateString?: string): string => {
	if (!dateString) return 'unknown_date';
	const date = new Date(dateString);
	return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

const requestHeaders: AxiosRequestConfig = {
	headers: {
		'Content-Type': 'application/json',
	},
	responseType: 'blob',
};

export const downloadBlob = (data: Blob, name: string, fileExt: string, date?: string) => {
	const url = window.URL.createObjectURL(data);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', `${name}${date ? '_' + date : ''}.${fileExt}`);
	document.body.appendChild(link);
	link.click();
	link.parentNode?.removeChild(link);
	window.URL.revokeObjectURL(url);
};
